import { useEffect, useState } from 'react';
import { FieldValues, Path } from 'react-hook-form';
import { AuthFormManagementProps } from '@/type/form.types';
import { AuthField } from '@/components/Form/Auth/AuthField';
import { AuthFieldContainer } from '@/components/Form/Auth/AuthFieldContainer';
import { Input } from '@/components/Controls/Input';
import { twMerge } from 'tailwind-merge';
import { AuthButton } from '@/components/Custom/Button/AuthButton';
import { AuthLinkButton } from '@/components/Custom/Button/AuthLinkButton';
import { ErrorMessage } from '@/components/Message/ErrorMessage';

export const ResetPasswordManagement = <TSchema extends FieldValues>({
  t,
  responseError,
  setResponseError,
  register,
  watch,
  errors,
  submitDisabled,
  onSubmit,
}: AuthFormManagementProps<TSchema>) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

  const passwordValue = watch && watch('password' as Path<TSchema>);
  const passwordConfirmValue =
    watch && watch('passwordConfirm' as Path<TSchema>);

  useEffect(() => {
    if (passwordValue || passwordConfirmValue) {
      setResponseError?.('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passwordValue, passwordConfirmValue]);

  return (
    <form onSubmit={onSubmit}>
      <div className="flex flex-col gap-5">
        <AuthFieldContainer>
          <AuthField title={t('common:password')} required>
            <Input
              {...(register && register('password' as Path<TSchema>))}
              errorMessage={errors && (errors.password?.message as string)}
              placeholder={t('common:password')}
              type={showPassword ? 'text' : 'password'}
              rightIcon={
                <i
                  className={twMerge(
                    'ki-filled text-gray-500 text-xl hover:cursor-pointer',
                    showPassword ? 'ki-eye-slash' : 'ki-eye'
                  )}
                  onClick={() => setShowPassword((prevState) => !prevState)}
                />
              }
              inputClassName="h-10"
            />
          </AuthField>
        </AuthFieldContainer>
        <AuthFieldContainer>
          <AuthField title={t('common:confirmNewPassword')} required>
            <Input
              {...(register && register('passwordConfirm' as Path<TSchema>))}
              errorMessage={
                errors && (errors.passwordConfirm?.message as string)
              }
              placeholder={t('common:reEnterPassword')}
              type={showPasswordConfirm ? 'text' : 'password'}
              rightIcon={
                <i
                  className={twMerge(
                    'ki-filled text-gray-500 text-xl hover:cursor-pointer',
                    showPasswordConfirm ? 'ki-eye-slash' : 'ki-eye'
                  )}
                  onClick={() =>
                    setShowPasswordConfirm((prevState) => !prevState)
                  }
                />
              }
              inputClassName="h-10"
            />
          </AuthField>
        </AuthFieldContainer>
      </div>
      {responseError && (
        <div className="mt-5 flex justify-center">
          <ErrorMessage message={responseError} />
        </div>
      )}
      <div className="mt-7">
        <AuthButton text={t('common:confirm')} disabled={submitDisabled} />
      </div>
      <div className="mt-5">
        <AuthLinkButton linkTo="/login" text={t('common:cancel')} />
      </div>
    </form>
  );
};
